import React, { useState } from "react";
import type { Role } from "../auth";
import { DataToolbar } from "../components/DataToolbar";
import { Modal } from "../components/Modal";
import { exportToExcel } from "../export/excel";
import { FileUploadKit } from "../components/common/FileUploadKit";
import { useToast } from "../components/Toast";
import { useAuth } from "../context/AuthContext";
import { isSuperUser } from "../config/roles";
import { db, serverTimestamp } from "../../firebaseClient";
import { uploadLawyerFiles } from "../services/storageUpload";
import type { AttachmentMeta } from "../types/attachments";
import { useAiContext } from "../ai/AiContext";
import { createLawyer, updateLawyer, softDeleteLawyer } from "../services/lawyerService";
import type { Lawyer, LawyerRequest } from "../types/legalOps";

// ✅ 最小變更：不改 shared types，僅在本頁擴充 year 欄位
type LawyerEx = Lawyer & { year?: number };

// Removed local Type definitions in favor of shared types in legalOps.ts

export function LawyerCoopPage({ role }: { role: Role }) {
  const { user } = useAuth();
  // Only Super Users (KC, AD, Ops, Gm) can edit/manage lawyers
  const canManage = isSuperUser(user?.email);

  const [lawyers, setLawyers] = useState<LawyerEx[]>([]);
  const [requests, setRequests] = useState<LawyerRequest[]>([]);

  const [modalMode, setModalMode] = useState<"none" | "addLawyer" | "newRequest">("none");
  const [targetLawyer, setTargetLawyer] = useState<LawyerEx | null>(null); // For Requests
  const [editingLawyer, setEditingLawyer] = useState<LawyerEx | null>(null); // For Editing Lawyer

  const { showToast } = useToast();
  const { requestDelete } = useAiContext();

  React.useEffect(() => {
    const q1 = db.collection("legal_lawyers").orderBy("createdAt", "desc");
    const unsub1 = q1.onSnapshot(async (snapshot) => {
      const rawList = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as LawyerEx));

      // Filter out soft-deleted lawyers
      const activeList = rawList.filter((l) => !l.isDeleted);
      setLawyers(activeList);
    });

    const q2 = db.collection("lawyer_requests").orderBy("createdAt", "desc");
    const unsub2 = q2.onSnapshot((snapshot) => {
      setRequests(snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as LawyerRequest)));
    });

    return () => {
      unsub1();
      unsub2();
    };
  }, []);

  const handleDeleteLawyer = (lawyer: LawyerEx) => {
    // Use standard requestDelete to show confirmation modal
    requestDelete({
      targetType: "lawyer",
      targetId: lawyer.id,
      summary: `${lawyer.firmName} ${lawyer.counselName}`,
      onConfirm: async () => {
        try {
          // Call Soft Delete Service
          await softDeleteLawyer(lawyer.id);
          showToast("已刪除顧問律師");
          // UI update handled by onSnapshot listener
        } catch (e) {
          console.error(e);
          showToast("刪除失敗", "error");
        }
      },
    });
  };

  const handleDeleteRequest = async (id: string) => {
    if (!confirm("確定刪除此需求？")) return;
    await db.collection("lawyer_requests").doc(id).delete();
    showToast("已刪除");
  };

  const getUsedHours = (lawyerId: string) => {
    return requests
      .filter((r) => r.lawyerId === lawyerId)
      .reduce((acc, r) => acc + (Number(r.requiredHours) || 0), 0);
  };

  const currentYear = new Date().getFullYear();

  return (
    <div>
      <div className="row" style={{ marginBottom: 16, justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 14, fontWeight: "bold", color: "var(--muted)" }}>顧問律師管理</div>
        <div className="row" style={{ gap: 8 }}>
          {canManage && (
            <button
              className="smallbtn"
              onClick={() => {
                setEditingLawyer(null);
                setModalMode("addLawyer");
              }}
              style={{ background: "#fff", color: "#000", fontWeight: "bold" }}
            >
              + 新增律師
            </button>
          )}
          <button className="smallbtn" onClick={() => exportToExcel("Lawyer_Stats", lawyers)}>
            匯出
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gap: 24 }}>
        {lawyers.map((l) => {
          const usedHours = getUsedHours(l.id);
          const pct = Math.min(100, (usedHours / (l.annualQuotaHours || 1)) * 100);
          const lawyerReqs = requests.filter((r) => r.lawyerId === l.id);

          return (
            <div key={l.id} className="tile" style={{ padding: 20 }}>
              {/* Top: Info */}
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: "bold", color: "#fff", marginBottom: 6 }}>
                    {l.firmName}{" "}
                    <span style={{ fontSize: 16, color: "#ccc", marginLeft: 8 }}>{l.counselName}</span>
                  </div>
                  <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.6 }}>
                    <div>專長：{l.specialty}</div>
                    <div>聯絡：{l.contact}</div>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="badge" style={{ fontSize: 12, marginBottom: 8 }}>
                    {l.status}
                  </div>
                  {canManage && (
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      <button
                        className="smallbtn"
                        style={{ fontSize: 12 }}
                        onClick={() => {
                          setEditingLawyer(l);
                          setModalMode("addLawyer");
                        }}
                      >
                        編輯
                      </button>
                      <button
                        className="smallbtn"
                        style={{ fontSize: 12, color: "var(--danger)", borderColor: "var(--danger)" }}
                        onClick={() => handleDeleteLawyer(l)}
                      >
                        刪除
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Progress Bar */}
              <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 12, padding: 16, marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
                  {/* ✅ 顯示年度（西元） */}
                  <span>
                    年度：{l.year ?? currentYear}｜年度時數：{l.annualQuotaHours}h
                  </span>
                  <span>
                    已用：
                    <span style={{ color: pct > 80 ? "#fbbf24" : "var(--ok)", fontWeight: "bold" }}>
                      {usedHours}h
                    </span>{" "}
                    / 剩餘：{Math.max(0, l.annualQuotaHours - usedHours)}h
                  </span>
                </div>
                <div style={{ width: "100%", height: 8, background: "#333", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: pct > 100 ? "var(--danger)" : "var(--ok)" }} />
                </div>
              </div>

              {/* Bottom: Requests */}
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                    borderBottom: "1px solid #333",
                    paddingBottom: 8,
                  }}
                >
                  <span style={{ fontWeight: "bold", fontSize: 14 }}>本年度協助請求列表</span>
                  {canManage && (
                    <button
                      className="smallbtn"
                      style={{ fontSize: 12 }}
                      onClick={() => {
                        setTargetLawyer(l);
                        setModalMode("newRequest");
                      }}
                    >
                      + 新增本次需求
                    </button>
                  )}
                </div>

                {lawyerReqs.length === 0 ? (
                  <div className="p" style={{ textAlign: "center", padding: 10 }}>
                    無請求紀錄
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 10 }}>
                    {lawyerReqs.map((req) => (
                      <div
                        key={req.id}
                        style={{
                          background: "#222",
                          borderRadius: 8,
                          padding: 12,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: "bold", fontSize: 14 }}>{req.requestTitle}</div>
                          <div style={{ fontSize: 12, color: "var(--muted)", margin: "4px 0" }}>{req.requestSummary}</div>
                          <div style={{ fontSize: 12, color: "#aaa" }}>
                            時數: {req.requiredHours}h | 費用: NT${req.estimatedFee.toLocaleString()}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          {req.attachments.length > 0 && (
                            <span style={{ fontSize: 12, color: "#60a5fa" }}>📎 {req.attachments.length}</span>
                          )}
                          {canManage && (
                            <button
                              className="smallbtn"
                              style={{ padding: "2px 6px", color: "var(--danger)", borderColor: "var(--danger)" }}
                              onClick={() => handleDeleteRequest(req.id)}
                            >
                              刪除
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {lawyers.length === 0 && (
          <div className="p" style={{ textAlign: "center", padding: 40, color: "var(--muted)" }}>
            暫無顧問律師資料 (已刪除或未建立)
          </div>
        )}
      </div>

      {modalMode === "addLawyer" && (
        <LawyerForm
          initialData={editingLawyer}
          onClose={() => {
            setModalMode("none");
            setEditingLawyer(null);
          }}
          onSuccess={() => {
            showToast(editingLawyer ? "已更新顧問律師資料" : "已新增顧問律師");
            setModalMode("none");
            setEditingLawyer(null);
          }}
        />
      )}

      {modalMode === "newRequest" && targetLawyer && (
        <NewRequestModal
          counsel={targetLawyer as any}
          usedHours={getUsedHours(targetLawyer.id)}
          onClose={() => setModalMode("none")}
          onSuccess={() => {
            showToast("需求已建立");
            setModalMode("none");
          }}
        />
      )}
    </div>
  );
}

function LawyerForm({ initialData, onClose, onSuccess }: any) {
  const currentYear = new Date().getFullYear();

  const [form, setForm] = useState(
    initialData || {
      firmName: "",
      counselName: "",
      specialty: "",
      contact: "",
      status: "合作中",
      // ✅ 新增：年度（西元）
      year: currentYear,
      annualQuotaHours: 40,
      overQuotaRate: 6000,
      notes: "",
    }
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async () => {
    if (!form.firmName) return alert("請填寫事務所名稱");

    // ✅ 保底：沒填就用今年
    const normalized = {
      ...form,
      year: Number(form.year) || currentYear,
      annualQuotaHours: Number(form.annualQuotaHours) || 0,
      overQuotaRate: Number(form.overQuotaRate) || 0,
    };

    setIsSubmitting(true);
    try {
      if (initialData?.id) {
        await updateLawyer(initialData.id, normalized);
      } else {
        await createLawyer({
          ...normalized,
          attachments: [],
        });
      }
      onSuccess();
    } catch (e) {
      console.error(e);
      alert("儲存失敗");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal title={initialData ? "編輯顧問律師" : "新增顧問律師"} onClose={onClose}>
      <div className="grid">
        <div className="field">
          <label className="label">事務所</label>
          <input className="input" value={form.firmName} onChange={(e) => setForm({ ...form, firmName: e.target.value })} />
        </div>

        <div className="field">
          <label className="label">律師姓名</label>
          <input
            className="input"
            value={form.counselName}
            onChange={(e) => setForm({ ...form, counselName: e.target.value })}
          />
        </div>

        <div className="field">
          <label className="label">專長</label>
          <input className="input" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} />
        </div>

        <div className="field">
          <label className="label">聯絡方式</label>
          <input className="input" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
        </div>

        {/* ✅ 新增：年度（西元） */}
        <div className="field">
          <label className="label">年度（西元）</label>
          <input
            type="number"
            className="input"
            min={2000}
            max={2100}
            step={1}
            value={form.year ?? currentYear}
            onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
            placeholder="例如：2025"
          />
        </div>

        <div className="field">
          <label className="label">年度時數 (hr)</label>
          <input
            type="number"
            className="input"
            value={form.annualQuotaHours}
            onChange={(e) => setForm({ ...form, annualQuotaHours: Number(e.target.value) })}
          />
        </div>

        <div className="field">
          <label className="label">超額費率 (NT$/hr)</label>
          <input
            type="number"
            className="input"
            value={form.overQuotaRate}
            onChange={(e) => setForm({ ...form, overQuotaRate: Number(e.target.value) })}
          />
        </div>

        <div className="field">
          <label className="label">合作狀態</label>
          <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="合作中">合作中</option>
            <option value="備選">備選</option>
            <option value="暫停合作">暫停合作</option>
          </select>
        </div>
      </div>

      <div className="row" style={{ marginTop: 20, justifyContent: "flex-end" }}>
        <button className="btn" onClick={handleSave} disabled={isSubmitting}>
          儲存
        </button>
      </div>
    </Modal>
  );
}

function NewRequestModal({
  counsel,
  usedHours,
  onClose,
  onSuccess,
}: {
  counsel: Lawyer;
  usedHours: number;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [title, setTitle] = useState("新商品代工合約條款檢視");
  const [summary, setSummary] = useState("請求律師協助審查新商品 OEM 合約中關於責任限制與驗收條款。");
  const [requiredHours, setRequiredHours] = useState(2);
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const remaining = Math.max(0, counsel.annualQuotaHours - usedHours);
  const paidHours = Math.max(0, requiredHours - remaining);
  const hourlyRate = paidHours > 0 ? (counsel.overQuotaRate || 6000) : 0;
  const estimatedFee = paidHours * hourlyRate;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const docRef = await db.collection("lawyer_requests").add({
        lawyerId: counsel.id,
        requestTitle: title,
        requestSummary: summary,
        requiredHours,
        hourlyRate,
        estimatedFee,
        status: "OPEN",
        createdAt: serverTimestamp(),
        attachments: [],
      });

      if (files.length > 0) {
        const uploaded = await uploadLawyerFiles(docRef.id, files);
        await docRef.update({ attachments: uploaded });
      }
      onSuccess();
    } catch (e) {
      console.error(e);
      alert("建立失敗");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal title={`新增需求 - ${counsel.firmName}`} onClose={onClose}>
      <div className="grid">
        <div className="field" style={{ gridColumn: "1/-1" }}>
          <label className="label">本次需求標題</label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="field" style={{ gridColumn: "1/-1" }}>
          <label className="label">需求摘要</label>
          <textarea className="input" rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} />
        </div>
        <div className="field">
          <label className="label">預估所需時數 (hr)</label>
          <input
            type="number"
            className="input"
            min={0.5}
            step={0.5}
            value={requiredHours}
            onChange={(e) => setRequiredHours(Number(e.target.value))}
          />
        </div>
        <div className="field">
          <label className="label">目前可用免費額度</label>
          <div className="p" style={{ marginTop: 8 }}>
            {remaining} 小時
          </div>
        </div>
      </div>

      <div style={{ background: "rgba(255,255,255,0.05)", padding: 12, borderRadius: 8, marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span>超額費率：</span>
          <span>NT$ {counsel.overQuotaRate.toLocaleString()} / hr</span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: "bold",
            color: estimatedFee > 0 ? "#fbbf24" : "var(--ok)",
          }}
        >
          <span>本次預估費用：</span>
          <span>NT$ {estimatedFee.toLocaleString()}</span>
        </div>
        <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>(若仍在年度可用時數內，費用為 0)</div>
      </div>

      <FileUploadKit
        label="上傳需求相關文件"
        onFilesSelected={(fs) => setFiles((p) => [...p, ...fs])}
        existingFiles={[]}
      />

      <div className="row" style={{ marginTop: 20, justifyContent: "flex-end" }}>
        <button className="btn" style={{ width: "auto" }} onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? "處理中..." : "確認建立需求"}
        </button>
      </div>
    </Modal>
  );
}
